/**
 * PixelCanvas.js
 * Haupt-Canvas-Komponente fuer den Pixel-Editor.
 * Unterstuetzt: Zeichnen, Zoom, Pan, Grid-Overlay, alle Tools.
 */

import React, { useRef, useEffect, useCallback, useState, forwardRef, useImperativeHandle } from 'react';

// Flood-Fill Algorithmus
function floodFill(imageData, startX, startY, fillColor, width, height) {
  const data = imageData.data;
  const targetIdx = (startY * width + startX) * 4;
  const targetR = data[targetIdx];
  const targetG = data[targetIdx + 1];
  const targetB = data[targetIdx + 2];
  const targetA = data[targetIdx + 3];

  const [fr, fg, fb, fa] = fillColor;

  // Gleiche Farbe -> nichts tun
  if (targetR === fr && targetG === fg && targetB === fb && targetA === fa) return;

  const stack = [[startX, startY]];
  const visited = new Uint8Array(width * height);

  while (stack.length > 0) {
    const [x, y] = stack.pop();
    if (x < 0 || x >= width || y < 0 || y >= height) continue;
    const idx = y * width + x;
    if (visited[idx]) continue;
    const pixIdx = idx * 4;
    if (data[pixIdx] !== targetR || data[pixIdx + 1] !== targetG ||
        data[pixIdx + 2] !== targetB || data[pixIdx + 3] !== targetA) continue;

    visited[idx] = 1;
    data[pixIdx] = fr;
    data[pixIdx + 1] = fg;
    data[pixIdx + 2] = fb;
    data[pixIdx + 3] = fa;

    stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
  }
}

// Linie zeichnen (Bresenham)
function getLinePixels(x0, y0, x1, y1) {
  const pixels = [];
  let dx = Math.abs(x1 - x0);
  let dy = Math.abs(y1 - y0);
  let sx = x0 < x1 ? 1 : -1;
  let sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;

  while (true) {
    pixels.push([x0, y0]);
    if (x0 === x1 && y0 === y1) break;
    let e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x0 += sx; }
    if (e2 < dx) { err += dx; y0 += sy; }
  }
  return pixels;
}

// Rechteck-Pixel
function getRectPixels(x0, y0, x1, y1, filled) {
  const pixels = [];
  const minX = Math.min(x0, x1);
  const maxX = Math.max(x0, x1);
  const minY = Math.min(y0, y1);
  const maxY = Math.max(y0, y1);

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      if (filled || x === minX || x === maxX || y === minY || y === maxY) {
        pixels.push([x, y]);
      }
    }
  }
  return pixels;
}

const PixelCanvas = forwardRef(({
  width = 64,
  height = 64,
  zoom = 8,
  tool = 'pencil',
  color = [0, 0, 0, 255],      // [r, g, b, a]
  showGrid = true,
  onPixelChange,                // Callback bei jedem Pixel (Live-Sync, KEIN History-Push)
  onStrokeEnd,                  // Callback am Ende eines Strichs (fuer History-Push)
  onColorPick,                  // Callback fuer Pipette
  rectFilled = false,
  symmetryMode = 'none',        // 'none' | 'horizontal' | 'vertical' | 'both'
  scrollToY,                    // optional: scroll to Y pixel position (for animation frames)
  style = {},
}, ref) => {
  const canvasRef = useRef(null);
  const gridCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const [imageData, setImageData] = useState(null);
  const [isPainting, setIsPainting] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef(null);
  const lastPanOffset = useRef({ x: 0, y: 0 });

  // Tool-Start-Koordinaten fuer Linie/Rechteck
  const toolStart = useRef(null);
  // Snapshot vor Linie/Rechteck (fuer Preview)
  const toolSnapshot = useRef(null);
  // Merkt ob waehrend mouseDown gemalt wurde (fuer onStrokeEnd)
  const didPaintRef = useRef(false);

  // ImageData initialisieren
  useEffect(() => {
    const data = new ImageData(width, height);
    // Transparent initialisieren
    setImageData(data);
  }, [width, height]);

  // Grid-Canvas rendern
  useEffect(() => {
    if (!gridCanvasRef.current || !showGrid) return;
    const canvas = gridCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const canvasWidth = width * zoom;
    const canvasHeight = height * zoom;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    if (zoom >= 4) {
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 0.5;
      for (let x = 0; x <= width; x++) {
        ctx.beginPath();
        ctx.moveTo(x * zoom, 0);
        ctx.lineTo(x * zoom, canvasHeight);
        ctx.stroke();
      }
      for (let y = 0; y <= height; y++) {
        ctx.beginPath();
        ctx.moveTo(0, y * zoom);
        ctx.lineTo(canvasWidth, y * zoom);
        ctx.stroke();
      }
    }
  }, [width, height, zoom, showGrid]);

  // Haupt-Canvas rendern wenn sich imageData aendert
  useEffect(() => {
    if (!canvasRef.current || !imageData) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = width * zoom;
    canvas.height = height * zoom;

    // Checkerboard Hintergrund fuer Transparenz
    drawCheckerboard(ctx, width * zoom, height * zoom);

    // Pixel zeichnen (skaliert)
    const offscreen = document.createElement('canvas');
    offscreen.width = width;
    offscreen.height = height;
    const offCtx = offscreen.getContext('2d');
    offCtx.putImageData(imageData, 0, 0);

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(offscreen, 0, 0, width * zoom, height * zoom);
  }, [imageData, width, height, zoom]);

  function drawCheckerboard(ctx, w, h) {
    const size = 8;
    for (let y = 0; y < h; y += size) {
      for (let x = 0; x < w; x += size) {
        ctx.fillStyle = ((x / size + y / size) % 2 === 0) ? '#ccc' : '#fff';
        ctx.fillRect(x, y, size, size);
      }
    }
  }

  function canvasToPixel(e) {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / zoom);
    const y = Math.floor((e.clientY - rect.top) / zoom);
    if (x < 0 || x >= width || y < 0 || y >= height) return null;
    return { x, y };
  }

  function setPixel(data, x, y, color) {
    if (x < 0 || x >= width || y < 0 || y >= height) return;
    const idx = (y * width + x) * 4;
    data[idx] = color[0];
    data[idx + 1] = color[1];
    data[idx + 2] = color[2];
    data[idx + 3] = color[3] ?? 255;
  }

  function applySymmetry(data, x, y, col) {
    setPixel(data, x, y, col);
    if (symmetryMode === 'horizontal' || symmetryMode === 'both') {
      setPixel(data, width - 1 - x, y, col);
    }
    if (symmetryMode === 'vertical' || symmetryMode === 'both') {
      setPixel(data, x, height - 1 - y, col);
    }
    if (symmetryMode === 'both') {
      setPixel(data, width - 1 - x, height - 1 - y, col);
    }
  }

  const applyToolAtPixel = useCallback((px, py, currentImageData) => {
    if (!currentImageData) return currentImageData;
    const newData = new ImageData(new Uint8ClampedArray(currentImageData.data), width, height);

    if (tool === 'pencil') {
      applySymmetry(newData.data, px, py, color);
    } else if (tool === 'eraser') {
      applySymmetry(newData.data, px, py, [0, 0, 0, 0]);
    } else if (tool === 'fill') {
      floodFill(newData, px, py, color, width, height);
    } else if (tool === 'picker') {
      const idx = (py * width + px) * 4;
      const r = currentImageData.data[idx];
      const g = currentImageData.data[idx + 1];
      const b = currentImageData.data[idx + 2];
      const a = currentImageData.data[idx + 3];
      if (onColorPick) onColorPick([r, g, b, a]);
      return currentImageData; // Kein Pixel-Change
    }

    didPaintRef.current = true;
    if (onPixelChange) onPixelChange(newData);
    return newData;
  }, [tool, color, width, height, onPixelChange, onColorPick, symmetryMode]); // eslint-disable-line

  const handleMouseDown = useCallback((e) => {
    if (e.button === 1 || e.button === 2) {
      // Mittlere/rechte Maustaste = Pan
      setIsPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY };
      lastPanOffset.current = { ...panOffset };
      return;
    }

    const px = canvasToPixel(e);
    if (!px) return;

    didPaintRef.current = false;

    if (tool === 'line' || tool === 'rect') {
      toolStart.current = px;
      toolSnapshot.current = imageData ? new ImageData(new Uint8ClampedArray(imageData.data), width, height) : null;
      setIsPainting(true);
      return;
    }

    setIsPainting(true);
    setImageData(prev => applyToolAtPixel(px.x, px.y, prev));
  }, [canvasToPixel, tool, imageData, applyToolAtPixel, panOffset, width, height]);

  const handleMouseMove = useCallback((e) => {
    if (isPanning && panStart.current) {
      const dx = e.clientX - panStart.current.x;
      const dy = e.clientY - panStart.current.y;
      setPanOffset({ x: lastPanOffset.current.x + dx, y: lastPanOffset.current.y + dy });
      return;
    }

    if (!isPainting) return;
    const px = canvasToPixel(e);
    if (!px) return;

    if (tool === 'line' && toolStart.current && toolSnapshot.current) {
      // Preview: Snapshot wiederherstellen + Linie einzeichnen
      const previewData = new ImageData(new Uint8ClampedArray(toolSnapshot.current.data), width, height);
      const linePixels = getLinePixels(toolStart.current.x, toolStart.current.y, px.x, px.y);
      for (const [lx, ly] of linePixels) applySymmetry(previewData.data, lx, ly, color);
      if (onPixelChange) onPixelChange(previewData);
      didPaintRef.current = true;
      setImageData(previewData);
      return;
    }

    if (tool === 'rect' && toolStart.current && toolSnapshot.current) {
      const previewData = new ImageData(new Uint8ClampedArray(toolSnapshot.current.data), width, height);
      const rectPixels = getRectPixels(toolStart.current.x, toolStart.current.y, px.x, px.y, rectFilled);
      for (const [rx, ry] of rectPixels) applySymmetry(previewData.data, rx, ry, color);
      if (onPixelChange) onPixelChange(previewData);
      didPaintRef.current = true;
      setImageData(previewData);
      return;
    }

    setImageData(prev => applyToolAtPixel(px.x, px.y, prev));
  }, [isPainting, isPanning, canvasToPixel, tool, applyToolAtPixel, toolStart, toolSnapshot, color, width, height, rectFilled, onPixelChange]);

  const handleMouseUp = useCallback((e) => {
    if (isPanning) {
      setIsPanning(false);
      panStart.current = null;
      return;
    }
    const wasPainting = isPainting;
    setIsPainting(false);
    toolStart.current = null;
    toolSnapshot.current = null;
    // Strich beendet: onStrokeEnd einmal aufrufen
    if (wasPainting && didPaintRef.current) {
      didPaintRef.current = false;
      setImageData(current => {
        if (onStrokeEnd && current) onStrokeEnd(current);
        return current;
      });
    }
  }, [isPanning, isPainting, onStrokeEnd]);

  // Imperatives API fuer Parent-Komponenten
  useImperativeHandle(ref, () => ({
    getImageData: () => imageData,
    setImageData: (data) => {
      setImageData(data);
    },
    getDataURL: () => {
      if (!imageData) return null;
      const offscreen = document.createElement('canvas');
      offscreen.width = width;
      offscreen.height = height;
      const ctx = offscreen.getContext('2d');
      ctx.putImageData(imageData, 0, 0);
      return offscreen.toDataURL('image/png');
    },
    loadFromDataURL: (dataURL) => {
      const img = new Image();
      img.onload = () => {
        const offscreen = document.createElement('canvas');
        offscreen.width = width;
        offscreen.height = height;
        const ctx = offscreen.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const newData = ctx.getImageData(0, 0, width, height);
        setImageData(newData);
        if (onPixelChange) onPixelChange(newData);
      };
      img.src = dataURL;
    },
    clear: () => {
      const newData = new ImageData(width, height);
      setImageData(newData);
      if (onPixelChange) onPixelChange(newData);
    },
    fill: (r, g, b, a = 255) => {
      const newData = new ImageData(width, height);
      for (let i = 0; i < newData.data.length; i += 4) {
        newData.data[i] = r;
        newData.data[i + 1] = g;
        newData.data[i + 2] = b;
        newData.data[i + 3] = a;
      }
      setImageData(newData);
      if (onPixelChange) onPixelChange(newData);
    },
  }), [imageData, width, height, onPixelChange]);

  // Auto-scroll to frame position for animation strips
  useEffect(() => {
    if (scrollToY !== undefined) {
      setPanOffset(prev => ({ x: prev.x, y: -(scrollToY * zoom) }));
    }
  }, [scrollToY, zoom]);

  const containerStyle = {
    position: 'relative',
    display: 'inline-block',
    overflow: 'hidden',
    cursor: tool === 'picker' ? 'crosshair' : tool === 'fill' ? 'cell' : 'crosshair',
    ...style,
  };

  const canvasContainerStyle = {
    transform: `translate(${panOffset.x}px, ${panOffset.y}px)`,
    position: 'relative',
    display: 'inline-block',
  };

  return (
    <div
      style={containerStyle}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onContextMenu={e => e.preventDefault()}
    >
      <div style={canvasContainerStyle}>
        {/* Haupt-Pixel-Canvas */}
        <canvas
          ref={canvasRef}
          style={{ display: 'block', imageRendering: 'pixelated' }}
        />
        {/* Grid-Overlay */}
        {showGrid && (
          <canvas
            ref={gridCanvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              pointerEvents: 'none',
              imageRendering: 'pixelated',
            }}
          />
        )}
        {/* Tool-Overlay fuer Preview */}
        <canvas
          ref={overlayCanvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
            imageRendering: 'pixelated',
          }}
        />
      </div>
    </div>
  );
});

PixelCanvas.displayName = 'PixelCanvas';
export default PixelCanvas;
